import cn from 'classnames';
import { useEffect, useRef, useState } from 'react';

import { hasNonWhiteTheme } from 'src/utils/hasNonWhiteTheme';

import { CoreInfographicGridData } from 'src/types/components';

import RichText from 'components/BaseHelpers/RichText';
import Text from 'components/BaseHelpers/Text';

import InfographicCard from './InfographicCard';

import styles from './CoreInfographicGrid.module.scss';

const CoreInfographicGrid = ({ fields }: CoreInfographicGridData): JSX.Element => {
  const ref = useRef(null);

  const { header, anchorTag, theme, statistics } = fields;
  const hasHeader = Object.values(header).some(Boolean);

  const [animateStats, setAnimateStats] = useState(false);

  const { eyebrow, heading, description } = header;

  const hasDarkerBackground = hasNonWhiteTheme(theme) && 'spacer--with-vertical-padding';

  const sectionClasses = cn(styles.main, 'spacer', hasDarkerBackground, styles[`theme--${theme}`]);

  const watchForAnimation: IntersectionObserverCallback = (entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        setAnimateStats(true);
      }
    });
  };

  useEffect(() => {
    const options = {
      rootMargin: '0px',
      threshold: 0.2,
    };
    const observer = new IntersectionObserver(watchForAnimation, options);

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      observer.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section id={anchorTag} className={sectionClasses}>
      <div className={cn('container', 'container--true-1440')}>
        {hasHeader && (
          <div className={styles.header}>
            {!!eyebrow && <Text tag="p" field={eyebrow} className={styles.eyebrow} />}
            {!!heading && <Text tag="h3" field={heading} className={styles.heading} />}
            {!!description && <RichText field={description} className={styles.description} />}
          </div>
        )}
        <ul className={cn(styles.cardList)} ref={ref}>
          {statistics.map((statistic, index) => {
            return (
              <li className={styles.cardItem} key={`infographic-card-${index}`}>
                <InfographicCard animate={animateStats} statistic={statistic}></InfographicCard>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
};

export default CoreInfographicGrid;
